import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { composeValidators, mustBeNumber } from 'components/helpers/validators';
import { useTranslation } from 'react-i18next';
import { Option, StatusOption } from 'shared/types/general';
import { ContractLine } from '../../types';
import { useMemo } from 'react';
import { buildContractLineStatusOptions } from './statusOption';
import { SectionHeader } from 'components/ui/typography';

interface UpdateContractLineFormParams {
  contractLine: ContractLine | any; // Represents the contract line data
  warehouses: Option[]; // List of warehouse options for the dropdown
  statusOptions?: StatusOption[]; // Optional list of status options. The form filters them based on the current row stock state.
  onSubmit: any; // Callback function to handle form submission
}

export const UpdateContractLineForm = ({
  contractLine,
  warehouses,
  statusOptions = [],
  onSubmit
}: UpdateContractLineFormParams) => {
  const { t } = useTranslation();

  // Function to handle form submission
  const onSubmitContractLine = async (values) => {
    // Prepare parameters for the submit function
    const params = {
      id: contractLine.id,
      unit_price: values.unit_price.replace(',', '.'), // Replace comma with dot for unit price
      initial_available_units: values.initial_available_units,
      initial_discounted_units: values.initial_discounted_units,
      iva: values.iva,
      warehouse: values.warehouse || null,
      order_notes: values.order_notes ?? '',
      status: values.status || contractLine.status || null
    };

    // Call the onSubmit callback with contract line ID and prepared parameters
    onSubmit(contractLine.id, params);
  };

  // KILO-CODE soluzione per il RICARICAMENTO dinamico del componente sulla base della riga di contratto aperta
  const statusOptionsList = useMemo(
    () =>
      buildContractLineStatusOptions(
        statusOptions,
        Boolean(contractLine?.has_stock_quantity),
        contractLine?.status,
        t
      ),
    [statusOptions, contractLine?.has_stock_quantity, contractLine?.status, t]
  );

  const initialStatusOption = useMemo(() => {
    if (contractLine.status === undefined || contractLine.status === null) return undefined;

    const statusAsString = String(contractLine.status);
    return statusOptionsList.find((opt) => {
      return (
        String(opt.value) === statusAsString ||
        String(opt.display_name) === statusAsString ||
        String(opt.display_name) === statusAsString ||
        String(opt.display_name ?? opt.display_name) === statusAsString
      );
    });
  }, [contractLine.status, statusOptionsList]);

  return (
    <EnhancedForm
      onSubmit={onSubmitContractLine}
      formId="updateContracLine"
      hideControls
      autoStyleLayout
      columns={4}>
      <IdentityField
        gridSpan={2}
        disabled
        component={TextFieldAdapter}
        id="outlined-disabled"
        name="product"
        label={t('global.product')}
        type="text"
        defaultValue={contractLine.product.code}
      />
      <IdentityField
        gridSpan={2}
        name="warehouse"
        component={AutocompleteAdapter}
        options={warehouses}
        optionValue="id"
        label={t('global.warehouse')}
        type="text"
        initValue={contractLine.warehouse}
        defaultValue={contractLine.warehouse}
      />
      <IdentityField
        gridSpan={4}
        name="order_notes"
        label={t('entities.product.order_notes')}
        component={TextFieldAdapter}
        type="text"
        defaultValue={contractLine.order_notes || ''}
        multiline
        rows={2}
      />
      <IdentityField
        name="unit_price"
        label={t('entities.contract_line.unit_price')}
        component={TextFieldAdapter}
        type="number"
        validate={composeValidators(mustBeNumber)}
        defaultValue={contractLine.unit_price}
      />
      <IdentityField
        name="initial_available_units"
        label={t('entities.contract_line.initial_available_units')}
        component={TextFieldAdapter}
        type="number"
        validate={composeValidators(mustBeNumber)}
        defaultValue={contractLine.initial_available_units}
      />
      <IdentityField
        name="initial_discounted_units"
        label={t('entities.contract_line.initial_discounted_units')}
        component={TextFieldAdapter}
        type="number"
        validate={composeValidators(mustBeNumber)}
        defaultValue={contractLine.initial_discounted_units}
      />
      <IdentityField
        name="iva"
        label={t('entities.contract_line.iva')}
        component={TextFieldAdapter}
        type="number"
        validate={composeValidators(mustBeNumber)}
        defaultValue={contractLine.iva}
      />
      <SectionHeader
        title={t('entities.contract_line.thresholds-edit-status-selection')}
        gridSpan
      />
      <IdentityField
        name="status"
        component={AutocompleteAdapter}
        initValue={initialStatusOption}
        options={statusOptionsList}
        optionLabel="display_name"
        optionValue="value"
        label={t('entities.product.status')}
        type="text"
      />
    </EnhancedForm>
  );
};
